import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useStore } from '@/hooks/useStore';
import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { flowResult } from 'mobx';
import { useEffect, useState } from 'react';
import { Option } from '@/types/option';
import { FORM_TYPE, FormType, OPTION_FORMAT, STATUS } from '@/types/enums';
import { SelectBox } from '@/components/common/SelectBox';
import { OPTION_FORMAT_CONFIG } from '@/configs/constants';
import { PostOptionRequest } from '@/types/http-payload/option';
import { useLocation } from 'react-router-dom';
import { isPathEk } from '@/utils/routes';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';

interface CreateOptionProps {
    optionId?: number | string,
    onRefresh?: Function,
    formType: FormType
}

const CreateOptionForm = observer(({ optionId, onRefresh, formType }: CreateOptionProps) => {
    // hook
    const { t } = useTranslation();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);

    const OPTIONS = !isEk ?
        Object.keys(OPTION_FORMAT_CONFIG).filter(key => [OPTION_FORMAT.Option.valueOf().toString(), OPTION_FORMAT.Insurance.valueOf().toString()].includes(key))
            .map(key => ({
                label: t(`field.${OPTION_FORMAT_CONFIG[key].key}`),
                value: OPTION_FORMAT_CONFIG[key].value.toString()
            })) :
        Object.keys(OPTION_FORMAT_CONFIG).filter(key => [OPTION_FORMAT.Option.valueOf().toString()].includes(key))
            .map(key => ({
                label: t(`field.${OPTION_FORMAT_CONFIG[key].key}`),
                value: OPTION_FORMAT_CONFIG[key].value.toString()
            }));

    // store
    const {
        modalStore: { hideAllModals, showAlertModal, hideModal },
        optionStore: { createOption, getOptionById, updateOption, deleteOption }
    } = useStore();

    const validateSchema = yup.object().shape({
        name: yup
            .string().trim()
            .required('form_error_validate.required'),
        price: yup.string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .when(['format'], {
                is: (format) => {
                    return format !== OPTION_FORMAT.DropOff.valueOf();
                },
                then: schema => schema.test(
                    'min-value',
                    t('form_error_validate.min', { min: 0 }),
                    (val) => {
                        if (val == null || val === '') {
                            return true;
                        }
                        const num = Number(val);
                        return num >= 0;
                    }
                ).required('form_error_validate.required'),
                otherwise: schema => schema.nullable()
            }),
        format: yup.number()
            .required('form_error_validate.required'),
        maxValue: yup.string().trim()
            .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
            .max(2, t('form_error_validate.max_length', { length: 2 }))
            .when(['format'], {
                is: (format) => {
                    return [OPTION_FORMAT.Option.valueOf(), OPTION_FORMAT.ExtraMileage.valueOf()].includes(format);
                },
                then: schema => schema.required('form_error_validate.required'),
                otherwise: schema => schema.nullable()
            }),
        status: yup.number()
            .required('form_error_validate.required')
    });

    type OptionFormData = yup.InferType<typeof validateSchema>;

    const form = useForm<OptionFormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
            price: '',
            // format: undefined,
            // typeValue: OPTION_TYPE_VALUE.YesNo,
            maxValue: '',
            status: STATUS.Active
        }
    });

    // Watch
    const formatValue = form.watch('format');
    // const typeValue = form.watch('typeValue');
    // const isShowMaxValue = typeValue === OPTION_TYPE_VALUE.Number;

    // state
    const [option, setOption] = useState<Option>();
    // lifecyle
    useEffect(() => {
        fetchOption();
    }, [optionId]);

    useEffect(() => {
        if (optionId && option) {
            form.reset({
                name: option?.name,
                price: option?.price?.toString(),
                format: option?.format,
                status: option?.status,
                maxValue: option?.maxValue?.toString() ?? ''
            });
        }
    }, [option]);

    const fetchOption = async () => {
        if (optionId) {
            const res = await flowResult(getOptionById(optionId));
            setOption(res);
        }
    };

    const onSubmit = async (data: OptionFormData) => {
        const { name, price: priceStr, format, status, maxValue: maxStr } = data;
        const price = Number(priceStr);
        const maxValue = Number(maxStr);
        const validData = { name, price, format, status };
        if ([OPTION_FORMAT.Option.valueOf(), OPTION_FORMAT.ExtraMileage.valueOf()].includes(format) && maxValue) {
            validData['maxValue'] = maxValue;
        }
        hideAllModals();
        if (optionId) {
            onUpdateOption(optionId, validData);
        } else {
            onCreateOption(validData);
        }
    };

    const onCreateOption = async (data: PostOptionRequest) => {
        const res = await flowResult(createOption(data));
        if (res?.ok) {
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('messages.add_success'),
                onClickXIcon: () => {
                    onRefresh && onRefresh();
                }
            });
        }
    };

    const onUpdateOption = async (optionId: string | number, data: PostOptionRequest) => {
        const res = await flowResult(updateOption(optionId, data));
        if (res?.ok) {
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('messages.update_success'),
                onClickXIcon: () => {
                    onRefresh && onRefresh();
                }
            });
        }
    };

    const onCancel = () => {
        hideAllModals();
    };

    const onDelete = () => {
        if (optionId) {
            showAlertModal({
                id: 'delete-optional',
                isConfirmModal: true,
                content: t('messages.confirm_delete'),
                onSave: () => {
                    onDeleteOptionById(optionId);
                    hideModal('delete-optional');
                }
            });
        }
    };

    const onDeleteOptionById = async (optionId: number | string) => {
        const res = await flowResult(deleteOption(optionId));
        if (res?.ok) {
            hideAllModals();
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('messages.delete_success'),
                onClickXIcon: () => {
                    onRefresh && onRefresh();
                }
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='flex flex-col gap-4 text-start'>
                    <FormField
                        control={form.control}
                        name='name'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>{t('field.option_page.option_name')}</FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('placeholder.enter')}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    {
                        formatValue !== OPTION_FORMAT.DropOff && (
                            <FormField
                                control={form.control}
                                name='price'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.fee', { currency: '' })}</FormLabel>
                                        <FormControl>
                                            <FormatNumberVer2
                                                decimalScale={0}
                                                prefix={t('currency.currency_symbol')}
                                                statusDisplay='input'
                                                thousandSeparator={true}
                                                allowNegative={false}
                                                placeholder={t('placeholder.enter')}
                                                value={field.value}
                                                setvalueOfNumericFormat={(values) => {
                                                    field.onChange(values);
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )
                    }

                    <div className='flex gap-4 flex-wrap'>
                        <FormField
                            control={form.control}
                            name='format'
                            render={({ field }) => (
                                <FormItem className='flex-1 min-w-[320px]'>
                                    <FormLabel required>{t('field.format')}</FormLabel>
                                    <FormControl>
                                        <SelectBox
                                            items={OPTIONS}
                                            defaultValue={field.value?.toString()}
                                            onSelected={(option) => {
                                                field.onChange(parseInt(option.value));
                                                form.setValue('format', parseInt(option.value));
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {
                            [OPTION_FORMAT.Option.valueOf(), OPTION_FORMAT.ExtraMileage.valueOf()].includes(formatValue) && (
                                <div className='flex gap-4 flex-1 min-w-[320px]'>
                                    <FormField
                                        control={form.control}
                                        name='maxValue'
                                        render={({ field }) => (
                                            <FormItem className='w-full'>
                                                <FormLabel className='' required>{t('placeholder.max_value')}</FormLabel>
                                                <FormControl>
                                                    <FormatNumberVer2
                                                        decimalScale={0}
                                                        statusDisplay='input'
                                                        thousandSeparator={true}
                                                        allowNegative={false}
                                                        placeholder={t('placeholder.max_value')}
                                                        value={field.value}
                                                        setvalueOfNumericFormat={(values) => {
                                                            field.onChange(values);
                                                        }}
                                                        maxLength={2}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            )
                        }
                    </div>
                    <FormField
                        control={form.control}
                        name='status'
                        render={({ field }) => {
                            return (
                                <FormItem className='flex'>
                                    <FormLabel className='mb-0' required>{t('field.status')}</FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            className='flex gap-4 flex-1 justify-evenly'
                                            defaultValue={field.value.toString()}
                                            value={field.value.toString()}
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                                form.setValue('status', parseInt(value));
                                            }}
                                        >
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem value={STATUS.Active.toString()} id='active' />
                                                <Label htmlFor='active' className='cursor-pointer font-normal'>{t('field.active')}</Label>
                                            </div>
                                            <div className='flex items-center space-x-2'>
                                                <RadioGroupItem value={STATUS.Inactive.toString()} id='inactive' />
                                                <Label htmlFor='inactive' className='cursor-pointer font-normal'>{t('field.inactive')}</Label>
                                            </div>
                                        </RadioGroup>
                                    </FormControl>
                                </FormItem>
                            );
                        }}
                    />
                </div>
                <div className='flex gap-4 justify-center mt-8'>
                    {
                        optionId ?
                            (
                                // <ButtonCus
                                //     variant='outline'
                                //     size='inModal'
                                //     type='button'
                                //     onClick={onDelete}
                                // >
                                //     {t('button.delete')}
                                // </ButtonCus>
                                <ButtonCusWithIcon
                                    variant='outline'
                                    size='inModal'
                                    type='button'
                                    iconId='#icon-delete'
                                    onClick={onDelete}
                                >
                                    {t('button.delete')}
                                </ButtonCusWithIcon>
                            ) :
                            (
                                <ButtonCus variant='outline' size='inModal' type='button' onClick={onCancel}>{t('button.cancel')}</ButtonCus>
                            )
                    }
                    {formType === FORM_TYPE.Create ?
                        (
                            <ButtonCus
                                variant='default'
                                size='inModal'
                                type='submit'
                            >
                                {t('button.add')}
                            </ButtonCus>
                        ) :
                        (
                            // <ButtonCus variant='default' size='inModal' type='submit'>
                            //     {t('button.update')}
                            // </ButtonCus>
                            <ButtonCusWithIcon
                                type='submit'
                                variant='default'
                                size='inModal'
                                iconId='#icon-update'
                            >
                                {t('button.update')}
                            </ButtonCusWithIcon>
                        )}
                </div>
            </form>
        </Form>
    );
});

export default CreateOptionForm;
